/**
 * Form controller creation for molecule.dev.
 *
 * @module
 */
import { t } from '@molecule/app-i18n';
import { snapshotSubmissionValue } from './snapshots.js';
import { formValuesEqual, snapshotFormValue } from './utilities.js';
import { validateValue } from './validation.js';
/**
 * Creates a native form provider that manages form state, validation,
 * and field registration without any external library. This is the
 * built-in default used when no form library bond is configured.
 *
 * Default validation messages are translated through `@molecule/app-i18n`'s `t` when they
 * are produced, so they follow the bonded locale.
 *
 * @returns A `FormProvider` backed by vanilla JavaScript state management.
 */
export const createNativeFormProvider = () => {
    return {
        createForm(options) {
            const mode = options.mode || 'onSubmit';
            // Late records always combine with these original defaults, never with
            // an earlier record. Detach each baseline and the live values so nested
            // caller mutations cannot rewrite clean or validated snapshots.
            // Runtime form values are always a plain string-keyed object; the caller's
            // values TYPE may be an interface without an index signature, so the
            // implementation holds the widened record view and casts back to `T`
            // only where it hands values out.
            const originalDefaultValues = snapshotFormValue((options.defaultValues ?? {}));
            let defaultValues = snapshotFormValue({
                ...originalDefaultValues,
                ...(options.values ?? {}),
            });
            let values = snapshotFormValue(defaultValues);
            let errors = {};
            let touched = {};
            let submitCount = 0;
            let isSubmitting = false;
            let replacementRevision = 0;
            let validationOrder = 0;
            let submissionVersion = 0;
            let submissionValidationVersion = 0;
            let destroyed = false;
            // Value revisions track content, never validation start order. Each field
            // owns its validation independently; whole-form operations own only the
            // fields they actually validate.
            const fieldSchemas = new Map();
            const fieldValueRevisions = new Map();
            const fieldValidationOwners = new Map();
            const fieldRefs = new Map();
            const listeners = new Set();
            const isFieldRevisionCurrent = (name, revision, replacement) => !destroyed &&
                replacement === replacementRevision &&
                revision === (fieldValueRevisions.get(name) ?? 0);
            const isFieldCurrent = (name, revision, replacement, validation) => isFieldRevisionCurrent(name, revision, replacement) &&
                validation === fieldValidationOwners.get(name);
            const isSubmissionCurrent = (submission, validation) => !destroyed && submission === submissionVersion && validation === submissionValidationVersion;
            const advanceValueRevision = (names) => {
                for (const name of names) {
                    fieldValueRevisions.set(name, (fieldValueRevisions.get(name) ?? 0) + 1);
                }
            };
            const reviseChangedValues = (previous, next) => {
                const names = new Set([...Object.keys(previous), ...Object.keys(next)]);
                const changed = [...names].filter((name) => !formValuesEqual(previous[name], next[name]));
                if (changed.length > 0)
                    advanceValueRevision(changed);
            };
            const recordIdentityChanged = (previous, next) => {
                const previousId = previous.id;
                return previousId !== undefined && previousId !== null && !Object.is(previousId, next.id);
            };
            const validationFields = (snapshot) => new Set([...Object.keys(snapshot), ...fieldSchemas.keys(), ...Object.keys(errors)]);
            const beginValidation = (fields) => {
                const validation = ++validationOrder;
                for (const name of fields)
                    fieldValidationOwners.set(name, validation);
                return validation;
            };
            const publishValidationErrors = (fields, revisions, replacement, validation, result) => {
                const published = {};
                const publishedNames = [];
                const newErrors = { ...errors };
                let didPublish = fields.size === 0 && replacement === replacementRevision && !destroyed;
                const ownsValidation = (name) => fields.has(name)
                    ? fieldValidationOwners.get(name) === validation
                    : !fieldValidationOwners.has(name);
                for (const name of new Set([...fields, ...Object.keys(result)])) {
                    if (!ownsValidation(name) ||
                        !isFieldRevisionCurrent(name, revisions.get(name) ?? 0, replacement)) {
                        continue;
                    }
                    didPublish = true;
                    publishedNames.push(name);
                    const error = result[name];
                    if (error) {
                        newErrors[name] = error;
                        published[name] = error;
                    }
                    else {
                        delete newErrors[name];
                    }
                }
                errors = newErrors;
                return {
                    errors: published,
                    didPublish,
                    isCurrent: () => publishedNames.every((name) => ownsValidation(name) &&
                        isFieldRevisionCurrent(name, revisions.get(name) ?? 0, replacement)),
                };
            };
            const notify = () => {
                const state = controller.getState();
                listeners.forEach((callback) => callback(state));
            };
            const shouldValidate = (event) => {
                if (mode === 'all')
                    return true;
                if (mode === 'onSubmit')
                    return event === 'submit';
                if (mode === 'onChange')
                    return event === 'change' || event === 'submit';
                if (mode === 'onBlur')
                    return event === 'blur' || event === 'submit';
                return false;
            };
            const validateSnapshot = async (snapshot, current) => {
                let isValid = true;
                const newErrors = {};
                for (const [name, schema] of fieldSchemas) {
                    const error = await validateValue(snapshot[name], schema, t);
                    if (!current())
                        return null;
                    if (error) {
                        newErrors[name] = error;
                        isValid = false;
                    }
                }
                if (options.validate) {
                    const formErrors = await options.validate(snapshot);
                    if (!current())
                        return null;
                    for (const [key, error] of Object.entries(formErrors)) {
                        if (error) {
                            newErrors[key] = error;
                            isValid = false;
                        }
                    }
                }
                return { isValid, errors: newErrors };
            };
            const controller = {
                getState() {
                    return {
                        values: values,
                        errors,
                        touched,
                        isValid: Object.keys(errors).length === 0,
                        isDirty: !formValuesEqual(values, defaultValues),
                        isSubmitting,
                        submitCount,
                    };
                },
                getValue(name) {
                    return values[name];
                },
                getValues() {
                    return { ...values };
                },
                setValue(name, value, opts = {}) {
                    advanceValueRevision([name]);
                    values = { ...values, [name]: snapshotFormValue(value) };
                    if (opts.shouldTouch !== false) {
                        touched = { ...touched, [name]: true };
                    }
                    if (opts.shouldValidate && shouldValidate('change')) {
                        this.validateField(name);
                    }
                    notify();
                },
                setValues(newValues, opts = {}) {
                    advanceValueRevision(Object.keys(newValues));
                    values = { ...values, ...snapshotFormValue(newValues) };
                    if (opts.shouldValidate) {
                        this.validate();
                    }
                    notify();
                },
                getError(name) {
                    return errors[name];
                },
                setError(name, error) {
                    errors = { ...errors, [name]: error };
                    notify();
                },
                clearError(name) {
                    const newErrors = { ...errors };
                    delete newErrors[name];
                    errors = newErrors;
                    notify();
                },
                clearErrors() {
                    errors = {};
                    notify();
                },
                getFieldState(name) {
                    return {
                        value: values[name],
                        error: errors[name],
                        touched: touched[name] || false,
                        dirty: !formValuesEqual(values[name], defaultValues[name]),
                        valid: !errors[name],
                        validating: false,
                    };
                },
                register(opts) {
                    const { name, defaultValue, transform, ...schema } = opts;
                    // Store validation schema
                    fieldSchemas.set(name, schema);
                    // A registered default is also clean baseline content. Keep it with
                    // the original defaults so later replacement records can restore it.
                    if (defaultValue !== undefined) {
                        if (originalDefaultValues[name] === undefined) {
                            originalDefaultValues[name] = snapshotFormValue(defaultValue);
                        }
                        if (defaultValues[name] === undefined) {
                            defaultValues = { ...defaultValues, [name]: snapshotFormValue(defaultValue) };
                        }
                        if (values[name] === undefined) {
                            advanceValueRevision([name]);
                            values = { ...values, [name]: snapshotFormValue(defaultValue) };
                        }
                    }
                    return {
                        name,
                        value: values[name],
                        onChange: (event) => {
                            let newValue;
                            if (event && typeof event === 'object' && 'target' in event) {
                                const target = event.target;
                                newValue = target.value;
                            }
                            else {
                                newValue = event;
                            }
                            if (transform) {
                                newValue = transform(newValue);
                            }
                            this.setValue(name, newValue, {
                                shouldValidate: shouldValidate('change'),
                            });
                        },
                        onBlur: () => {
                            touched = { ...touched, [name]: true };
                            if (shouldValidate('blur')) {
                                this.validateField(name);
                            }
                            notify();
                        },
                        ref: (element) => {
                            if (element) {
                                fieldRefs.set(name, element);
                            }
                            else {
                                fieldRefs.delete(name);
                            }
                        },
                    };
                },
                unregister(name) {
                    advanceValueRevision([name]);
                    fieldSchemas.delete(name);
                    fieldRefs.delete(name);
                    const newValues = { ...values };
                    delete newValues[name];
                    values = newValues;
                    const newErrors = { ...errors };
                    delete newErrors[name];
                    errors = newErrors;
                    const newTouched = { ...touched };
                    delete newTouched[name];
                    touched = newTouched;
                    notify();
                },
                async validateField(name) {
                    const key = name;
                    const schema = fieldSchemas.get(key);
                    if (!schema)
                        return true;
                    const revision = fieldValueRevisions.get(key) ?? 0;
                    const replacement = replacementRevision;
                    const validation = beginValidation([key]);
                    const current = () => isFieldCurrent(key, revision, replacement, validation);
                    const snapshot = snapshotFormValue(values[key]);
                    const error = await validateValue(snapshot, schema, t);
                    if (!current())
                        return false;
                    if (error) {
                        errors = { ...errors, [name]: error };
                    }
                    else {
                        const newErrors = { ...errors };
                        delete newErrors[name];
                        errors = newErrors;
                    }
                    notify();
                    return current() && !error;
                },
                async validate() {
                    const replacement = replacementRevision;
                    const snapshot = snapshotFormValue(values);
                    const fields = validationFields(snapshot);
                    const revisions = new Map([...fields].map((name) => [name, fieldValueRevisions.get(name) ?? 0]));
                    const fieldValidation = beginValidation(fields);
                    const result = await validateSnapshot(snapshot, () => !destroyed);
                    if (!result || destroyed)
                        return false;
                    const publication = publishValidationErrors(fields, revisions, replacement, fieldValidation, result.errors);
                    if (publication.didPublish)
                        notify();
                    return result.isValid;
                },
                updateValues(newValues, resetOptions = {}) {
                    const nextDefaultValues = snapshotFormValue({
                        ...originalDefaultValues,
                        ...newValues,
                    });
                    if (formValuesEqual(nextDefaultValues, defaultValues))
                        return;
                    const currentValues = values;
                    const previousDefaultValues = defaultValues;
                    if (resetOptions.keepDirtyValues !== true ||
                        recordIdentityChanged(previousDefaultValues, nextDefaultValues)) {
                        submissionValidationVersion++;
                        replacementRevision++;
                    }
                    const nextValues = snapshotFormValue(nextDefaultValues);
                    if (resetOptions.keepDirtyValues) {
                        for (const key of Object.keys(currentValues)) {
                            if (!formValuesEqual(currentValues[key], previousDefaultValues[key])) {
                                nextValues[key] = snapshotFormValue(currentValues[key]);
                            }
                        }
                    }
                    reviseChangedValues(values, nextValues);
                    defaultValues = nextDefaultValues;
                    values = nextValues;
                    errors = {};
                    touched = {};
                    notify();
                },
                reset(newValues) {
                    // A reset establishes a clean baseline: with no argument it restores
                    // the current baseline; with values it merges them over that baseline
                    // and makes the result the new one, so freshly loaded or saved values
                    // read clean afterwards.
                    const nextDefaultValues = newValues === undefined
                        ? defaultValues
                        : snapshotFormValue({
                            ...defaultValues,
                            ...newValues,
                        });
                    if (recordIdentityChanged(defaultValues, nextDefaultValues)) {
                        submissionValidationVersion++;
                    }
                    const nextValues = snapshotFormValue(nextDefaultValues);
                    replacementRevision++;
                    reviseChangedValues(values, nextValues);
                    defaultValues = nextDefaultValues;
                    values = nextValues;
                    errors = {};
                    touched = {};
                    submitCount = 0;
                    isSubmitting = false;
                    notify();
                },
                handleSubmit(onSubmit, onError) {
                    return async (event) => {
                        if (event?.preventDefault) {
                            event.preventDefault();
                        }
                        const submission = ++submissionVersion;
                        const submissionValidation = ++submissionValidationVersion;
                        const replacement = replacementRevision;
                        submitCount++;
                        isSubmitting = true;
                        const submissionValues = snapshotSubmissionValue(values);
                        const fields = validationFields(submissionValues);
                        const revisions = new Map([...fields].map((name) => [name, fieldValueRevisions.get(name) ?? 0]));
                        const fieldValidation = beginValidation(fields);
                        const currentSubmission = () => isSubmissionCurrent(submission, submissionValidation);
                        try {
                            notify();
                            if (!currentSubmission())
                                return;
                            const result = await validateSnapshot(submissionValues, currentSubmission);
                            if (!result || !currentSubmission())
                                return;
                            const publication = publishValidationErrors(fields, revisions, replacement, fieldValidation, result.errors);
                            if (publication.didPublish) {
                                notify();
                                if (!currentSubmission())
                                    return;
                            }
                            if (result.isValid) {
                                await onSubmit(submissionValues);
                                if (!currentSubmission())
                                    return;
                            }
                            else {
                                if (Object.keys(publication.errors).length === 0 || !publication.isCurrent())
                                    return;
                                if (onError) {
                                    onError(publication.errors);
                                    if (!currentSubmission() || !publication.isCurrent())
                                        return;
                                }
                                if (options.shouldFocusError !== false) {
                                    const firstErrorField = Object.keys(publication.errors)[0];
                                    if (firstErrorField) {
                                        this.setFocus(firstErrorField);
                                    }
                                }
                            }
                        }
                        finally {
                            if (submission === submissionVersion) {
                                isSubmitting = false;
                                notify();
                            }
                        }
                    };
                },
                setFocus(name) {
                    const element = fieldRefs.get(name);
                    if (element && 'focus' in element) {
                        ;
                        element.focus();
                    }
                },
                subscribe(callback) {
                    listeners.add(callback);
                    return () => listeners.delete(callback);
                },
                destroy() {
                    destroyed = true;
                    submissionVersion++;
                    submissionValidationVersion++;
                    replacementRevision++;
                    listeners.clear();
                    fieldSchemas.clear();
                    fieldRefs.clear();
                },
            };
            return controller;
        },
    };
};
/**
 * Pre-instantiated native form provider, ready to use without calling `createNativeFormProvider()`.
 */
export const nativeProvider = createNativeFormProvider();
//# sourceMappingURL=controller.js.map